// Dark mode (AC-UX-9, D-38). With no choice stored, the CSS follows the
// system setting (prefers-color-scheme); the toggle stores a choice and sets
// data-theme on <html>, which overrides it. public/theme.js applies a stored
// choice before the page is drawn.
import { useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';
const KEY = 'flexpdf-theme';

function stored(): Theme | undefined {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : undefined;
  } catch {
    return undefined;
  }
}

const system = (): Theme => (window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');

/** The theme in effect, and a toggle that stores the other one. */
export function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>(() => stored() ?? system());
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    const follow = () => !stored() && setTheme(system());
    mq?.addEventListener('change', follow);
    return () => mq?.removeEventListener('change', follow);
  }, []);
  const toggle = () => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    try {
      localStorage.setItem(KEY, next);
    } catch {
      // Not remembered, but applied for this visit.
    }
    document.documentElement.dataset.theme = next;
    setTheme(next);
  };
  return [theme, toggle];
}
